import { useState, useEffect } from 'react';
import type { ThinClient } from '../types';

interface ThinClientFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  client?: ThinClient | null;
  tenants: Array<{ id: string; name: string }>;
  venues: Array<{ id: string; name: string; tenantId: string }>;
}

export default function ThinClientFormModal({ 
  isOpen, 
  onClose, 
  onSubmit, 
  client,
  tenants,
  venues
}: ThinClientFormModalProps) {
  const [formData, setFormData] = useState({
    alias: '',
    serialNumber: '',
    status: 'enabled' as 'enabled' | 'disabled',
    tenantId: '',
    venueId: '',
    macWireless: '',
    macWired: '',
    firmwareVersion: ''
  });

  const filteredVenues = formData.tenantId 
    ? venues.filter(v => v.tenantId === formData.tenantId)
    : [];

  useEffect(() => {
    if (client) {
      setFormData({
        alias: client.alias,
        serialNumber: client.serialNumber,
        status: client.status,
        tenantId: client.tenantId,
        venueId: client.venueId,
        macWireless: client.macWireless,
        macWired: client.macWired,
        firmwareVersion: client.firmwareVersion
      });
    } else {
      setFormData({
        alias: '',
        serialNumber: '',
        status: 'enabled',
        tenantId: '',
        venueId: '',
        macWireless: '',
        macWired: '',
        firmwareVersion: ''
      });
    }
  }, [client, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.alias.trim()) {
      alert('请输入终端别名');
      return;
    }
    if (!formData.serialNumber.trim()) {
      alert('请输入序列号SN');
      return;
    }
    if (!formData.tenantId) {
      alert('请选择租户');
      return;
    }
    if (!formData.venueId) {
      alert('请选择场所');
      return;
    }
    if (!formData.macWireless.trim()) {
      alert('请输入无线MAC地址');
      return;
    }
    if (!formData.macWired.trim()) {
      alert('请输入有线MAC地址');
      return;
    }
    if (!formData.firmwareVersion.trim()) {
      alert('请输入固件版本');
      return;
    }

    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {client ? '编辑硬件终端' : '新建硬件终端'}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                终端别名 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.alias}
                onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
                placeholder="请输入终端别名"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                序列号SN <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.serialNumber}
                onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                placeholder="请输入序列号SN"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                状态 <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    value="enabled"
                    checked={formData.status === 'enabled'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'enabled' | 'disabled' })}
                    className="w-4 h-4 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">启用</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    value="disabled"
                    checked={formData.status === 'disabled'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'enabled' | 'disabled' })}
                    className="w-4 h-4 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">停用</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                租户 <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.tenantId}
                onChange={(e) => {
                  setFormData({ ...formData, tenantId: e.target.value, venueId: '' });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">请选择租户</option>
                {tenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                场所 <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.venueId}
                onChange={(e) => setFormData({ ...formData, venueId: e.target.value })}
                disabled={!formData.tenantId}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
              >
                <option value="">请选择场所</option>
                {filteredVenues.map(venue => (
                  <option key={venue.id} value={venue.id}>{venue.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  无线MAC <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.macWireless}
                  onChange={(e) => setFormData({ ...formData, macWireless: e.target.value })}
                  placeholder="00:1A:2B:3C:4D:5E"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  有线MAC <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.macWired}
                  onChange={(e) => setFormData({ ...formData, macWired: e.target.value })}
                  placeholder="00:1A:2B:3C:4D:5F"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                固件版本 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.firmwareVersion}
                onChange={(e) => setFormData({ ...formData, firmwareVersion: e.target.value })}
                placeholder="v2.3.1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              取消
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
            >
              确定
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
